// At-a-glance verdict cards (explains-page-contract.json verdict_strip):
// verification outcome, gains-locked overshoot, absorbed vs detected, and
// the proposed-trade rollup. All values derive from payload fields already
// on the soft-completion view; cards whose driving data is absent render
// nothing (conditional_render).
import { Card, Col, Row } from "react-bootstrap";
import { formatCurrency } from "../../../../lib/numbers";
import type { SoftSectionContext } from "./SoftCompletionContext";
import { num } from "./softCompletionLib";

function VerdictCard({
  label,
  value,
  sub,
  bad,
}: {
  label: string;
  value: string;
  sub: string;
  bad?: boolean;
}) {
  return (
    <Col xs={12} sm={6} lg={3} className="mb-2">
      <Card
        className="h-100"
        style={{
          borderTop: `3px solid var(--bs-${bad ? "danger" : "success"})`,
        }}
      >
        <Card.Body className="py-2 px-3">
          <div className="text-uppercase text-muted fw-bold small">{label}</div>
          <div
            className={`fs-5 fw-bold font-monospace text-${bad ? "danger" : "success"}`}
          >
            {value}
          </div>
          <div className="text-muted small">{sub}</div>
        </Card.Body>
      </Card>
    </Col>
  );
}

export default function VerdictStripSection({
  ctx,
}: {
  ctx: SoftSectionContext;
}) {
  const { completion, accounts } = ctx;
  const verification = completion.verification;
  const overshoot = completion.overshoot_redistribution;
  if (verification == null && overshoot == null) return null;

  const detected =
    overshoot?.overshoot_by_ac.reduce((sum, r) => sum + num(r.detected), 0) ??
    0;
  const absorbed =
    overshoot?.overshoot_by_ac.reduce((sum, r) => sum + num(r.absorbed), 0) ??
    0;
  const residual =
    overshoot?.overshoot_by_ac.reduce((sum, r) => sum + num(r.residual), 0) ??
    0;
  const warnings = overshoot?.warnings ?? [];
  const blockingCount = warnings.reduce(
    (sum, w) => sum + w.blocking_positions.length,
    0,
  );

  let tradeCount = 0;
  let buyTotal = 0;
  let sellTotal = 0;
  let realizedSt = 0;
  let realizedLt = 0;
  for (const account of accounts) {
    tradeCount += account.trade_generation?.trades.length ?? 0;
    buyTotal += num(account.trade.buy_total);
    sellTotal += Math.abs(num(account.trade.sell_total));
    realizedSt += num(account.trade_generation?.realized_gain_st);
    realizedLt += num(account.trade_generation?.realized_gain_lt);
  }

  return (
    <Row className="mb-2">
      {verification != null && (
        <VerdictCard
          label="Verification"
          value={verification.passed ? "reached" : "not reached"}
          sub={`max deviation ${(num(verification.max_deviation) * 100).toFixed(2)}%`}
          bad={!verification.passed}
        />
      )}
      {warnings.length > 0 && (
        <VerdictCard
          label="Gains-locked overshoot"
          value={formatCurrency(
            warnings.reduce((sum, w) => sum + num(w.residual_dollars), 0),
          )}
          sub={`stays over target on ${warnings
            .map((w) => w.ac)
            .join(
              ", ",
            )} · ${blockingCount} blocking position${blockingCount === 1 ? "" : "s"}`}
          bad
        />
      )}
      {overshoot != null && detected > 0 && (
        <VerdictCard
          label="Overshoot absorbed"
          value={`${formatCurrency(absorbed)} / ${formatCurrency(detected)}`}
          sub={`${formatCurrency(detected)} detected · ${formatCurrency(residual)} residual`}
          bad={false}
        />
      )}
      <VerdictCard
        label="Proposed trades"
        value={String(tradeCount)}
        sub={`buy ${formatCurrency(buyTotal)} · sell ${formatCurrency(sellTotal)} · realized ST ${formatCurrency(realizedSt)} / LT ${formatCurrency(realizedLt)}`}
        bad={false}
      />
    </Row>
  );
}
